import { usePerms } from 'contexts/PermsContext';
import { useAPIData } from 'hooks/useAPIData';
import useDebounce from 'hooks/useDebounce';
import { useEffect, useState } from 'react';
import { Button, Form } from 'react-bootstrap';
// removed unused selector; using effective location from `useLocation` instead
import useLocation from 'hooks/useLocation';
import { FaArrowLeft, FaFloppyDisk, FaTriangleExclamation } from 'react-icons/fa6';
import Popup from 'reactjs-popup';
import { autoCompleteSearch, cwAutoCompleteSearch } from 'services/location/location.service';
import ConfirmPopup from '../UI/ConfirmPopup';

const PlacesSearch = ({
  addLocation,
  selectLocation,
  setSearching,
  noBack,
  quickSave,
  quickLook,
  cwValidated,
}) => {
  const { locations } = useLocation();
  const effectiveLocation = locations.effectiveLocation;
  const [searchResults, setSearchResults] = useState([]);
  const [query, setQuery] = useState('');
  const [dropDownOpen, setDropDownOpen] = useState(false);
  const [tempLocation, setTempLocation] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [showWaitingForData, setShowWaitingForData] = useState(false);
  const [waitingForDataMessage, setWaitingForDataMessage] = useState('Loading...');
  const debouncedSearchTerm = useDebounce(query, 1000);
  const FusionData = useAPIData();
  const perms = usePerms();

  useEffect(() => {
    if (debouncedSearchTerm) {
      (async () => {
        searchAutoFill(debouncedSearchTerm);
      })();
    }
  }, [debouncedSearchTerm]);

  const getAddressComponent = (location, componentType) => {
    return location?.place_data?.address_components?.find((component) =>
      component?.types?.includes(componentType),
    );
  };

  const formatSelectedLocationLabel = (location) => {
    if (!location) return '';

    const city =
      location.city ||
      getAddressComponent(location, 'locality')?.long_name ||
      getAddressComponent(location, 'postal_town')?.long_name ||
      '';
    const county =
      location.county ||
      getAddressComponent(location, 'administrative_area_level_2')?.long_name ||
      '';
    const stateShort =
      location.state_short ||
      getAddressComponent(location, 'administrative_area_level_1')?.short_name ||
      '';
    const stateLong =
      location.state_long ||
      getAddressComponent(location, 'administrative_area_level_1')?.long_name ||
      '';
    const state = stateShort || stateLong;

    const isCountySearchResult =
      location.type === 'administrative_area_level_2' ||
      location?.place_data?.types?.includes('administrative_area_level_2');

    if (city && state && county && !isCountySearchResult) {
      return `${city}, ${state}, ${county}`;
    }

    if (city && state) {
      return `${city}, ${state}`;
    }

    if (isCountySearchResult && state) {
      return county ? `${county}, ${state}` : state;
    }

    if (county && state) {
      return `${county}, ${state}`;
    }

    return location.description || location.title || location.term || '';
  };

  const searchAutoFill = async (query) => {
    let searchLocation = query.trim();
    if (searchLocation === '') {
      setSearchResults([]);
      return;
    }
    setFetching(true);
    setSearchResults([]);

    try {
      // When `cwValidated`, hit the CW-aware endpoint so each result carries
      // `cwConfirmed` / `city_id` and we can flag picks that won't populate.
      const search = cwValidated ? cwAutoCompleteSearch : autoCompleteSearch;
      let results = await search(
        effectiveLocation.coords[0],
        effectiveLocation.coords[1],
        searchLocation,
      );
      results = results.data.result;

      if (results) {
        setSearchResults(results);
        setFetching(false);
      } else {
        setFetching(false);
        setSearchResults([]);
      }
    } catch (error) {
      console.error('Error fetching autocomplete data:', error);
      setFetching(false);
      setSearchResults([]);
    }
  };

  const tempLookAtLocation = (location) => {
    if (!location) return;

    setTempLocation(location);

    if (selectLocation && typeof selectLocation === 'function') {
      selectLocation(location);
    }
  };

  const resetSearch = () => {
    setQuery('');
    setSearchResults([]);
    setDropDownOpen(false);
    setFetching(false);
  };

  const stopSearching = () => {
    resetSearch();
    setSearching(false);
  };

  const saveLocation = (location) => {
    if (!location) return;
    addLocation(location);
    setSearching(false);
  };

  return (
    <div>
      <search className="d-flex z-1" role="search" aria-label="Location search">
        <Popup
          trigger={
            <div
              className="d-flex justify-content-between rounded-5 px-2 py-1 fs-6 z-1 bg-primary-highlight"
              style={{ minWidth: 170 }}
              onClick={(e) => {
                e.stopPropagation();
                setDropDownOpen(true);
              }}
            >
              {!noBack && (
                <Button onClick={stopSearching} aria-label="Back">
                  <FaArrowLeft aria-hidden="true" />
                </Button>
              )}
              <Form.Control
                className="ms-2 w-100 bg-transparent border-0"
                type="text"
                role="combobox"
                aria-label="Search for a location"
                aria-autocomplete="list"
                aria-expanded={dropDownOpen}
                aria-controls="places-search-results"
                placeholder="Search for a location..."
                value={query}
                onClick={(e) => {
                  e.stopPropagation();
                  setDropDownOpen(true);
                }}
                onFocus={() => setDropDownOpen(true)}
                onKeyDown={(e) => {
                  e.stopPropagation();

                  if (e.key === 'Escape') {
                    e.preventDefault();
                    resetSearch();
                    if (!noBack) setSearching(false);
                  }
                }}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setDropDownOpen(true);
                }}
              />
              {perms.testPermission([8, 9, 10, 11, 12, 13], ['Create-EditPlaces']) &&
                tempLocation &&
                !quickSave && (
                  <div
                    className="cursor-pointer"
                    role="button"
                    tabIndex={0}
                    aria-label="Save location"
                    onClick={() => saveLocation(tempLocation)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        saveLocation(tempLocation);
                      }
                    }}
                  >
                    <FaFloppyDisk aria-hidden="true" />
                  </div>
                )}
            </div>
          }
          open={dropDownOpen}
          onOpen={() => setDropDownOpen(true)}
          onClose={() => setDropDownOpen(false)}
          position={'bottom center'}
          closeOnDocumentClick
          closeOnEscape
        >
          {dropDownOpen && (
            <ul
              id="places-search-results"
              role="listbox"
              aria-label="Location search results"
              className="d-flex flex-column p-1 m-0 rounded-2 bg-primary z-1 list-unstyled"
              style={{ width: 200 }}
            >
              {searchResults.length === 0 && fetching && (
                <li className="p-1" role="status" aria-live="polite">
                  Loading...
                </li>
              )}
              {searchResults.length === 0 && !fetching && (
                <li className="p-1" role="status" aria-live="polite">
                  No results found
                </li>
              )}
              {searchResults.map((item, index) => {
                // Only meaningful on the CW-validated endpoint, which sets
                // `cwConfirmed`. Flag (but still allow) picks with no CW
                // city_id — they won't populate weather data.
                const unconfirmed = cwValidated && item.cwConfirmed === false;
                const selectResult = () => {
                  const selectedLocation = searchResults[index];
                  setQuery(formatSelectedLocationLabel(selectedLocation));
                  setDropDownOpen(false);
                  setSearchResults([]);
                  if (quickLook) tempLookAtLocation(selectedLocation);
                  if (quickSave) addLocation(selectedLocation);
                };
                return (
                  <li
                    key={index}
                    role="option"
                    aria-selected={false}
                    tabIndex={0}
                    className="btn btn-secondary p-1 d-flex align-items-center justify-content-between gap-2"
                    style={{
                      borderBottom: index === searchResults.length - 1 ? 'none' : '1px solid white',
                    }}
                    onClick={selectResult}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        selectResult();
                      }
                    }}
                  >
                    <span className="text-truncate">{item.description || 'none'}</span>
                    {unconfirmed && (
                      <FaTriangleExclamation
                        role="img"
                        aria-label="No weather data available for this location — it may not populate."
                        className="flex-shrink-0 text-warning"
                      />
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Popup>
      </search>
      {showWaitingForData && (
        <ConfirmPopup
          open={showWaitingForData}
          setOpen={setShowWaitingForData}
          message={waitingForDataMessage}
          options={['Cancel']}
          callbacks={[
            () => {
              setShowWaitingForData(false);
              FusionData.cancelLocationInquiry();
            },
          ]}
          closeAction={() => {
            setShowWaitingForData(false);
            FusionData.cancelLocationInquiry();
          }}
          buttonStyles={[
            { backgroundColor: 'var(--color-background)', color: 'var(--color-font)' },
          ]}
        />
      )}
    </div>
  );
};

export default PlacesSearch;
